import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useGetGlobalSettingsQuery, useUpdateGlobalSettingsMutation } from '../redux/adminApiSlice';
import type { GlobalSettings as GlobalSettingsType } from '../types/interfaces';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface FormData {
  locationUpdateInterval: string;
  promoteThreshold: string;
  allowMultiplePromotes: boolean;
  searchRadius: string;
  placeVisibilityRadius: string;
  placeEntryRadius: string;
  placeSearchLimit: string;
  placePhotoMaxWidth: string;
  usersPerPage: string;
  useDefaultUserCounts: boolean;
  defaultUserCounts: {
    neighborhood: string;
    city: string;
    state: string;
    country: string;
  };
  userStatusThresholds: {
    online: string;
    recentlyActive: string;
    inactive: string;
  };
  aggregateCityNeighborhoods: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const GlobalSettingsPanel = ({ setSnackbar }: { setSnackbar: (state: { open: boolean; message: string; severity: 'success' | 'error' }) => void }) => {
  const { data: settings, isLoading, error } = useGetGlobalSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] = useUpdateGlobalSettingsMutation();
  const [currentTab, setCurrentTab] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    locationUpdateInterval: '',
    promoteThreshold: '',
    allowMultiplePromotes: false,
    searchRadius: '',
    placeVisibilityRadius: '',
    placeEntryRadius: '',
    placeSearchLimit: '',
    placePhotoMaxWidth: '',
    usersPerPage: '',
    useDefaultUserCounts: true,
    defaultUserCounts: {
      neighborhood: '',
      city: '',
      state: '',
      country: ''
    },
    userStatusThresholds: {
      online: '',
      recentlyActive: '',
      inactive: ''
    },
    aggregateCityNeighborhoods: false
  });

  React.useEffect(() => {
    if (settings) {
      setFormData({
        locationUpdateInterval: Math.round(settings.locationUpdateInterval / 60000).toString(),
        promoteThreshold: settings.promoteThreshold?.toString() || '',
        allowMultiplePromotes: settings.allowMultiplePromotes || false,
        searchRadius: settings.searchRadius?.toString() || '',
        placeVisibilityRadius: settings.placeVisibilityRadius?.toString() || '',
        placeEntryRadius: settings.placeEntryRadius?.toString() || '',
        placeSearchLimit: settings.placeSearchLimit?.toString() || '',
        placePhotoMaxWidth: settings.placePhotoMaxWidth?.toString() || '',
        usersPerPage: settings.usersPerPage?.toString() || '61',
        useDefaultUserCounts: settings.useDefaultUserCounts ?? true,
        defaultUserCounts: {
          neighborhood: settings.defaultUserCounts?.neighborhood?.toString() || '',
          city: settings.defaultUserCounts?.city?.toString() || '',
          state: settings.defaultUserCounts?.state?.toString() || '',
          country: settings.defaultUserCounts?.country?.toString() || ''
        },
        userStatusThresholds: {
          online: settings.userStatusThresholds?.online?.toString() || '15',
          recentlyActive: settings.userStatusThresholds?.recentlyActive?.toString() || '60',
          inactive: settings.userStatusThresholds?.inactive?.toString() || '360'
        },
        aggregateCityNeighborhoods: settings.aggregateCityNeighborhoods ?? false
      });
    }
  }, [settings]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    if (e.target.name.startsWith('defaultUserCounts.')) {
      const field = e.target.name.split('.')[1] as keyof typeof formData.defaultUserCounts;
      setFormData({
        ...formData,
        defaultUserCounts: {
          ...formData.defaultUserCounts,
          [field]: value
        }
      });
    } else if (e.target.name.startsWith('userStatusThresholds.')) {
      const field = e.target.name.split('.')[1] as keyof typeof formData.userStatusThresholds;
      setFormData({
        ...formData,
        userStatusThresholds: {
          ...formData.userStatusThresholds,
          [field]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [e.target.name]: value
      });
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate numeric fields
      const validateNumber = (value: string, min: number, max: number | null, fieldName: string) => {
        const num = Number(value);
        if (isNaN(num) || num < min) {
          throw new Error(`${fieldName} must be at least ${min}`);
        }
        if (max !== null && num > max) {
          throw new Error(`${fieldName} must be at most ${max}`);
        }
        return num;
      };

      // Validate user status thresholds
      const onlineMinutes = validateNumber(formData.userStatusThresholds.online, 1, 60, 'Online threshold');
      const recentlyActiveMinutes = validateNumber(formData.userStatusThresholds.recentlyActive, onlineMinutes, 120, 'Recently active threshold');
      const inactiveMinutes = validateNumber(formData.userStatusThresholds.inactive, recentlyActiveMinutes, 1440, 'Inactive threshold');

      const updatedSettings: GlobalSettingsType = {
        locationUpdateInterval: validateNumber(formData.locationUpdateInterval, 1, 60, 'Location update interval') * 60000,
        promoteThreshold: validateNumber(formData.promoteThreshold, 0, 1, 'Promote threshold'),
        allowMultiplePromotes: formData.allowMultiplePromotes,
        searchRadius: validateNumber(formData.searchRadius, 0.01, 1, 'Search radius'),
        placeVisibilityRadius: validateNumber(formData.placeVisibilityRadius, 100, 5000, 'Place visibility radius'),
        placeEntryRadius: validateNumber(formData.placeEntryRadius, 50, 1000, 'Place entry radius'),
        placeSearchLimit: validateNumber(formData.placeSearchLimit, 1, null, 'Place search limit'),
        placePhotoMaxWidth: validateNumber(formData.placePhotoMaxWidth, 100, 2000, 'Place photo max width'),
        usersPerPage: validateNumber(formData.usersPerPage, 1, 100, 'Users per page'),
        useDefaultUserCounts: formData.useDefaultUserCounts,
        defaultUserCounts: {
          neighborhood: validateNumber(formData.defaultUserCounts.neighborhood, 1, null, 'Neighborhood user count'),
          city: validateNumber(formData.defaultUserCounts.city, 1, null, 'City user count'),
          state: validateNumber(formData.defaultUserCounts.state, 1, null, 'State user count'),
          country: validateNumber(formData.defaultUserCounts.country, 1, null, 'Country user count')
        },
        userStatusThresholds: {
          online: onlineMinutes,
          recentlyActive: recentlyActiveMinutes,
          inactive: inactiveMinutes
        },
        aggregateCityNeighborhoods: formData.aggregateCityNeighborhoods
      };

      await updateSettings(updatedSettings).unwrap();
      setSnackbar({
        open: true,
        message: 'Settings updated successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update settings',
        severity: 'error'
      });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">Error loading settings</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Global Settings
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="settings tabs">
          <Tab label="General" {...a11yProps(0)} />
          <Tab label="Places" {...a11yProps(1)} />
          <Tab label="Users" {...a11yProps(2)} />
        </Tabs>
      </Box>

      <TabPanel value={currentTab} index={0}>
        <Box component="form" noValidate autoComplete="off">
          <TextField
            fullWidth
            margin="normal"
            label="Location Update Interval (minutes)"
            name="locationUpdateInterval"
            value={formData.locationUpdateInterval}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1, max: 60 }}
            helperText="How often user locations are updated (1-60 minutes)"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Promote Threshold"
            name="promoteThreshold"
            value={formData.promoteThreshold}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 0, max: 1, step: 0.1 }}
            helperText="Threshold for post promotion (0-1)"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Search Radius"
            name="searchRadius"
            value={formData.searchRadius}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 0.01, max: 1, step: 0.01 }}
            helperText="Maximum search radius in degrees (0.01-1)"
          />
          
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Content Aggregation
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.aggregateCityNeighborhoods}
                onChange={handleInputChange}
                name="aggregateCityNeighborhoods"
              />
            }
            label="Aggregate Neighborhood Posts in City View"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, city-level feeds will include all posts from neighborhoods within that city. This helps populate city feeds when the user base is small.
          </Typography>
        </Box>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <Box component="form" noValidate autoComplete="off">
          <TextField
            fullWidth
            margin="normal"
            label="Place Visibility Radius (feet)"
            name="placeVisibilityRadius"
            value={formData.placeVisibilityRadius}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 100, max: 5000 }}
            helperText="Maximum distance at which places are visible (100-5000 feet)"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Place Entry Radius (feet)"
            name="placeEntryRadius"
            value={formData.placeEntryRadius}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 50, max: 1000 }}
            helperText="Distance within which users can enter a place (50-1000 feet)"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Place Search Limit"
            name="placeSearchLimit"
            value={formData.placeSearchLimit}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1 }}
            helperText="Maximum number of places to return in search (minimum 1, no upper limit)"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Place Photo Max Width (pixels)"
            name="placePhotoMaxWidth"
            value={formData.placePhotoMaxWidth}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 100, max: 2000 }}
            helperText="Maximum width for place photos (100-2000 pixels)"
          />
        </Box>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <Box component="form" noValidate autoComplete="off">
          <Typography variant="subtitle2" gutterBottom>
            User Grid Settings
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Users Per Page"
            name="usersPerPage"
            value={formData.usersPerPage}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1, max: 100 }}
            helperText="Number of users to display per page in the user grid (1-100)"
          />

          <Typography variant="subtitle2" sx={{ mt: 4, mb: 1 }}>
            Default User Counts
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.useDefaultUserCounts}
                onChange={handleInputChange}
                name="useDefaultUserCounts"
              />
            }
            label="Use Default User Counts"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, the system will use the default user counts below instead of actual user counts
          </Typography>

          <TextField
            fullWidth
            margin="normal"
            label="Default Neighborhood Users"
            name="defaultUserCounts.neighborhood"
            value={formData.defaultUserCounts.neighborhood}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1 }}
            disabled={!formData.useDefaultUserCounts}
            helperText="Default number of users for neighborhoods"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Default City Users"
            name="defaultUserCounts.city"
            value={formData.defaultUserCounts.city}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1 }}
            disabled={!formData.useDefaultUserCounts}
            helperText="Default number of users for cities"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Default State Users"
            name="defaultUserCounts.state"
            value={formData.defaultUserCounts.state}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1 }}
            disabled={!formData.useDefaultUserCounts}
            helperText="Default number of users for states"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Default Country Users"
            name="defaultUserCounts.country"
            value={formData.defaultUserCounts.country}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1 }}
            disabled={!formData.useDefaultUserCounts}
            helperText="Default number of users for countries"
          />

          <Typography variant="subtitle2" sx={{ mt: 4, mb: 1 }}>
            User Status Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure the time thresholds for user status indicators (in minutes)
          </Typography>
          
          <TextField
            fullWidth
            margin="normal"
            label="Online Threshold (Green)"
            name="userStatusThresholds.online"
            value={formData.userStatusThresholds.online}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1, max: 60 }}
            helperText="Time until user status changes from online (green) to recently active (1-60 minutes)"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Recently Active Threshold (Yellow)"
            name="userStatusThresholds.recentlyActive"
            value={formData.userStatusThresholds.recentlyActive}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1, max: 120 }}
            helperText="Time until user status changes from recently active (yellow) to inactive (1-120 minutes)"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Inactive Threshold (Grey)"
            name="userStatusThresholds.inactive"
            value={formData.userStatusThresholds.inactive}
            onChange={handleInputChange}
            type="number"
            inputProps={{ min: 1, max: 1440 }}
            helperText="Time until user is removed from grid (1-1440 minutes, max 24 hours)"
          />
        </Box>
      </TabPanel>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isUpdating}
        >
          {isUpdating ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default function Settings({ 
  useRemoteApi, 
  toggleApiEndpoint, 
  apiUrl 
}: { 
  useRemoteApi: boolean; 
  toggleApiEndpoint: () => void; 
  apiUrl: string | undefined 
}) {
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ width: '100%', mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="settings tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Global Settings" {...a11yProps(0)} />
            <Tab label="API Settings" {...a11yProps(1)} />
          </Tabs>

          <TabPanel value={currentTab} index={0}>
            <GlobalSettingsPanel setSnackbar={setSnackbar} />
          </TabPanel>
          
          <TabPanel value={currentTab} index={1}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                API Connection Settings
              </Typography>
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Current API URL
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    bgcolor: 'background.paper', 
                    p: 1, 
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                  }}
                >
                  {apiUrl || 'Not set'}
                </Typography>
              </Paper>
              
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  API Connection Mode
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Switch between local and remote API endpoints. Changing this setting will reload the application.
                </Typography>
                <Button
                  variant="contained"
                  onClick={toggleApiEndpoint}
                  sx={{
                    bgcolor: useRemoteApi ? 'success.main' : 'primary.main',
                    '&:hover': {
                      bgcolor: useRemoteApi ? 'success.dark' : 'primary.dark',
                    }
                  }}
                  startIcon={useRemoteApi ? '🌎' : '💻'}
                >
                  {useRemoteApi ? 'Using Remote API - Click to Switch to Local' : 'Using Local API - Click to Switch to Remote'}
                </Button>
              </Box>
            </Box>
          </TabPanel>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}
